import React from 'react';
import { Link } from 'react-router-dom';
import { HOME_LOANS_VISIBLE } from '../../lib/homeLoans';
import { Facebook, Linkedin, Mail, ShieldCheck, ArrowUpRight } from 'lucide-react';
import { ENTITY } from '../../lib/entity';

/**
 * Contact — Phase 99A light direction.
 *
 * Single-point-of-contact panel. Replaces the prior dark-navy block. Uses
 * the warm-paper panel surface so it slots cleanly into the rest of the
 * public site.
 */
export const Contact = () => {
  const email = ENTITY.contact.general;
  const subject = ENTITY.mailSubjects.general;

  return (
    <section
      className="py-24"
      style={{ background: 'var(--gpc-paper)', borderTop: '1px solid var(--gpc-line-soft)' }}
      data-testid="contact-section"
    >
      <div className="relative max-w-3xl mx-auto px-6">
        <div className="text-center">
          <p className="gpc-eyebrow" style={{ marginBottom: '14px' }}>Get in touch</p>
          <h2
            style={{
              fontFamily: 'Fraunces, "Playfair Display", Georgia, serif',
              fontWeight: 400,
              fontSize: 'clamp(1.875rem, 4vw, 2.6rem)',
              lineHeight: 1.15,
              letterSpacing: '-0.01em',
              color: 'var(--gpc-ink)',
            }}
          >
            One point of contact for every{' '}
            <span style={{ color: 'var(--gpc-gold)', fontStyle: 'italic' }}>enquiry.</span>
          </h2>
          <p
            style={{
              fontFamily: 'Inter, system-ui, sans-serif',
              fontSize: '1rem',
              color: 'var(--gpc-ink-soft)',
              marginTop: '20px',
              maxWidth: '32rem',
              marginLeft: 'auto',
              marginRight: 'auto',
              lineHeight: 1.6,
            }}
          >
            Choose the enquiry that fits your requirements. Home Loans and Private Capital are handled through separate pathways.
          </p>
        </div>

        <div className="company-division-grid mt-8" data-testid="contact-intents">
          {HOME_LOANS_VISIBLE && <article className="company-panel"><h3 className="gpc-h3">Home Loans</h3><p className="gpc-body mt-3">Buying, refinancing or investing. View the pathway in preparation and available calculators.</p><Link to="/home-loans" className="gpc-cta-secondary mt-5">Explore Home Loans →</Link></article>}
          <article className="company-panel"><h3 className="gpc-h3">Private Capital</h3><p className="gpc-body mt-3">Property, corporate or structured finance for institutional and sophisticated borrowers.</p><Link to="/for-borrowers" className="gpc-cta-secondary mt-5">Submit a Capital Enquiry →</Link></article>
          <article className="company-panel"><h3 className="gpc-h3">Capital Partners</h3><p className="gpc-body mt-3">Institutional mandate access and controlled private-capital distribution.</p><Link to="/for-lenders" className="gpc-cta-secondary mt-5">Capital Partner Enquiry →</Link></article>
          <article className="company-panel"><h3 className="gpc-h3">General</h3><p className="gpc-body mt-3">Business, platform and compliance questions.</p><a href={`mailto:${email}?subject=${encodeURIComponent(subject)}`} className="gpc-cta-secondary mt-5">General Enquiry →</a></article>
        </div>

        <div className="mt-10 text-center"><Link to="/book-appointment" className="gpc-cta-primary">Book an appointment →</Link><p className="gpc-body mt-3">Arrange an introductory conversation with GP Capital.</p></div>
        <div className="mt-12 flex flex-col items-center">
          <a
            href={`mailto:${email}?subject=${encodeURIComponent(subject)}`}
            className="group inline-flex items-center gap-3"
            style={{
              fontFamily: 'Space Grotesk, system-ui, sans-serif',
              fontWeight: 600,
              fontSize: 'clamp(1.25rem, 2.4vw, 1.75rem)',
              color: 'var(--gpc-ink)',
              borderBottom: '1px solid rgba(140,107,46,0.45)',
              paddingBottom: '8px',
              transition: 'color 0.2s ease, border-color 0.2s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--gpc-gold)'; e.currentTarget.style.borderColor = 'var(--gpc-gold)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--gpc-ink)'; e.currentTarget.style.borderColor = 'rgba(140,107,46,0.45)'; }}
            data-testid="contact-primary-email"
          >
            <Mail className="w-5 h-5 md:w-6 md:h-6" style={{ color: 'var(--gpc-gold)' }} />
            <span className="tracking-tight">{email}</span>
            <ArrowUpRight className="w-5 h-5 md:w-6 md:h-6 opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </a>
          <p style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: '12px', color: 'var(--gpc-ink-soft)', marginTop: '14px' }}>
            Enquiries are reviewed by the GP Capital team
          </p>
        </div>

        <div className="flex items-center justify-center gap-8 mt-12 pt-8" style={{ borderTop: '1px solid var(--gpc-line-soft)' }}>
          <a
            href={ENTITY.social.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm"
            style={{ color: 'var(--gpc-ink-soft)', transition: 'color 0.2s ease' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--gpc-ink)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--gpc-ink-soft)'; }}
            data-testid="contact-linkedin"
          >
            <Linkedin className="w-4 h-4" /> LinkedIn
          </a>
          <span aria-hidden="true" style={{ height: '16px', width: '1px', background: 'var(--gpc-line)' }} />
          <a
            href={ENTITY.social.facebook}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm"
            style={{ color: 'var(--gpc-ink-soft)', transition: 'color 0.2s ease' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--gpc-ink)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--gpc-ink-soft)'; }}
            data-testid="contact-facebook"
          >
            <Facebook className="w-4 h-4" /> Facebook
          </a>
        </div>

        <p
          style={{
            fontFamily: 'Inter, system-ui, sans-serif',
            fontSize: '11px',
            color: 'var(--gpc-ink-soft)',
            textAlign: 'center',
            marginTop: '28px',
          }}
          className="flex items-center justify-center gap-1.5"
        >
          <ShieldCheck className="w-3 h-3" />
          {ENTITY.legalWithAcn} · Private Capital: wholesale / sophisticated counterparties only
        </p>
      </div>
    </section>
  );
};

export default Contact;
